import { Link, useNavigate } from 'react-router';
import type { ReservedAction, ReservedActionKind } from '../lib/api-client';
import { formatDateTime } from '../utils/datetime';
import { getStatusBadgeClass } from '../utils/status-colors';
import { truncateText } from '../utils/text';

// 一覧本文の表示上限。マジックナンバー化を避けるため定数として定義。
const CONTENT_MAX_LENGTH = 100;
const AT_URI_MAX_LENGTH = 60;
const DID_MAX_LENGTH = 30;

const KIND_LABELS: Record<ReservedActionKind, string> = {
  POST: 'Post',
  REPOST: 'Repost',
  DELETE_POST: 'Delete'
};

const KIND_ICONS: Record<ReservedActionKind, string> = {
  POST: '📝',
  REPOST: '🔁',
  DELETE_POST: '🗑️'
};

const KIND_BADGE_CLASSES: Record<ReservedActionKind, string> = {
  POST: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  REPOST: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  DELETE_POST: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
};

interface ReservedActionListItemProps {
  action: ReservedAction;
  // グローバル一覧（全ユーザー横断の Reserved Actions ページ）でのみ true にし、
  // 投稿者情報を表示する。ユーザー詳細ページ側の見た目は変えない。
  showAuthor?: boolean;
  // 予約アクション（REPOST / DELETE_POST）が PENDING のときのみキャンセルボタンを表示する。
  // 未指定の場合はボタンを表示しない（呼び出し側でキャンセル操作をサポートしない場合）。
  onCancel?: (action: ReservedAction) => void;
  // キャンセル処理中の対象アクション ID。一致する行のみボタンを無効化し、二重送信を防ぐ。
  cancellingActionId?: string | null;
}

/**
 * 予約投稿・予約リポスト・予約削除を統合表示するリストアイテム。
 * kind === 'POST' のときのみ詳細ページ（PostDetailPage）へのリンクを持つ。
 * 予約アクション（REPOST / DELETE_POST）は詳細ページが存在しないためリンクしない。
 */
export function ReservedActionListItem({
  action,
  showAuthor = false,
  onCancel,
  cancellingActionId = null
}: ReservedActionListItemProps) {
  const navigate = useNavigate();
  const isLinkable = action.kind === 'POST';
  // 管理者によるキャンセルの対象は「実行前の予約リポスト / 予約削除」のみ。
  // 予約投稿（POST）は既存のキャンセル動線（PostDetailPage 等）に任せる。
  const isCancelable =
    (action.kind === 'REPOST' || action.kind === 'DELETE_POST') && action.status === 'PENDING';
  const isCancelling = cancellingActionId === action.id;

  const handleClick = () => {
    if (isLinkable) {
      navigate(`/reserved-actions/${action.id}`);
    }
  };

  const hasContent = action.kind === 'POST' ? !!action.text : !!action.targetPostText;
  const displayContent =
    action.kind === 'POST'
      ? (action.text ?? '')
      : (action.targetPostText ?? '対象ポストを取得できません');

  return (
    <div
      onClick={isLinkable ? handleClick : undefined}
      className={`border-b pb-4 pt-4 transition-colors last:border-b-0 dark:border-gray-700 ${
        isLinkable ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50' : ''
      }`}
      role={isLinkable ? 'button' : undefined}
      tabIndex={isLinkable ? 0 : undefined}
      onKeyDown={e => {
        if (isLinkable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {showAuthor && action.author && (
        <div className='mb-3 flex items-center'>
          {action.author.avatar && (
            <img
              src={action.author.avatar}
              alt={action.author.handle}
              className='mr-2 h-8 w-8 rounded-full'
            />
          )}
          <div className='flex-1'>
            <Link
              to={`/users/${action.userId ?? ''}`}
              className='text-sm font-medium text-gray-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400'
              onClick={e => e.stopPropagation()}
            >
              @{action.author.handle}
            </Link>
            <div className='text-xs text-gray-500 dark:text-gray-400'>
              {truncateText(action.author.did, DID_MAX_LENGTH)}
            </div>
          </div>
        </div>
      )}

      <div className='mb-2 flex items-center justify-between gap-2'>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${KIND_BADGE_CLASSES[action.kind]}`}
        >
          <span aria-hidden='true'>{KIND_ICONS[action.kind]}</span>
          {KIND_LABELS[action.kind]}
        </span>
        <span
          className={`rounded-full px-2 py-1 text-xs font-semibold ${getStatusBadgeClass(action.status)}`}
        >
          {action.status}
        </span>
      </div>

      <p
        className={`mb-2 text-sm ${
          hasContent
            ? 'text-gray-900 dark:text-gray-100'
            : 'text-gray-400 italic dark:text-gray-500'
        }`}
      >
        {truncateText(displayContent, CONTENT_MAX_LENGTH)}
      </p>

      {action.kind !== 'POST' && action.targetAtUri && (
        <p className='mb-1 text-xs text-gray-500 dark:text-gray-400'>
          Target:{' '}
          <span className='font-mono'>{truncateText(action.targetAtUri, AT_URI_MAX_LENGTH)}</span>
        </p>
      )}

      {action.kind === 'REPOST' && action.resultAtUri && (
        <p className='mb-1 text-xs text-gray-500 dark:text-gray-400'>
          Result:{' '}
          <span className='font-mono'>{truncateText(action.resultAtUri, AT_URI_MAX_LENGTH)}</span>
        </p>
      )}

      <p className='text-xs text-gray-500 dark:text-gray-400'>
        Scheduled:{' '}
        {formatDateTime(action.scheduledAt, { dateStyle: 'medium', timeStyle: 'short' }) || 'N/A'}
      </p>

      {action.errorMsg && (
        <p className='mt-1 text-xs text-red-600 dark:text-red-400'>Error: {action.errorMsg}</p>
      )}

      {isCancelable && onCancel && (
        <div className='mt-2'>
          <button
            type='button'
            onClick={e => {
              e.stopPropagation();
              onCancel(action);
            }}
            disabled={isCancelling}
            className='rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-red-900/20'
          >
            {isCancelling ? 'Cancelling...' : 'Stop Execution'}
          </button>
        </div>
      )}
    </div>
  );
}
